import React from 'react';
import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie-player';
import { appClient } from 'src/config/apollo';
import { toastNotificationsService } from 'src/config/rootService';
import { ScheduledContentActivityType } from 'src/generated/webapp_gql';
import { useGetScheduledContentActivityLazyQuery } from 'src/modules/scheduledContent/documents/getScheduledContentActivity.generated';
import { useRecordScheduledContentActivityMutation } from 'src/modules/scheduledContent/documents/recordScheduledContentActivity.generated';
import successAnimation from 'src/public/animations/colorful_confetti.json';
import { Button } from 'src/shared/components/Button';
import { Modal } from 'src/shared/components/Modal';
import { color, font } from 'src/shared/styles/variables';

export interface Props {
    contentUuid: string;
    show: boolean;
    onDismiss?: () => void;
}

// Return a random integer from 1, inclusive, through 6, inclusive.
const makeCelebrationTitleKeyIndex = () => {
    const min = 1;
    const max = 6;
    return Math.floor(Math.random() * max + min);
};

export const DailyPauseCelebrationModal: React.FC<Props> = ({
    contentUuid,
    show,
    onDismiss = () => undefined,
}) => {
    const [improvedWellbeing, setImprovedWellbeing] = useState<boolean | null>(null);
    const [celebrationTitleKeyIndex, setCelebrationTitleKeyIndex] = useState<number>(1);
    const { t } = useTranslation();

    const [
        getScheduledContentActivity,
        { data, loading },
    ] = useGetScheduledContentActivityLazyQuery({ client: appClient });
    const [recordScheduledContentActivityMutation] = useRecordScheduledContentActivityMutation({
        client: appClient,
    });

    useEffect(() => {
        getScheduledContentActivity();
        setImprovedWellbeing(null);
        setCelebrationTitleKeyIndex(makeCelebrationTitleKeyIndex());
    }, [contentUuid]);

    const onFoundHelpful = async (selectedYes: boolean) => {
        try {
            await recordScheduledContentActivityMutation({
                variables: {
                    recordScheduledContentActivityActivityType: selectedYes
                        ? ScheduledContentActivityType.Liked
                        : ScheduledContentActivityType.Disliked,
                    recordScheduledContentActivityScheduledContentUuid: contentUuid,
                },
            });
            setImprovedWellbeing(selectedYes);
            setTimeout(onDismiss, 500);
        } catch {
            toastNotificationsService.renderErrorToast(t('dailyPause:celebration_modal.cta_error'));
        }
    };

    return (
        <Modal
            visible={show}
            footer={null}
            closable
            onCancel={() => {
                onDismiss();
            }}
        >
            <ModalContainer>
                {!loading && !!data?.scheduledContentActivity && (
                    <ContentContainer>
                        <ConfettiContainer>
                            <SuccessAnimation animationData={successAnimation} loop play />
                            <ContentTitle>
                                {t([
                                    `dailyPause:celebration_modal.title.${celebrationTitleKeyIndex}`,
                                    `dailyPause:celebration_modal.title`,
                                ])}
                            </ContentTitle>
                            <CompletedCount>
                                {data.scheduledContentActivity.completedCount}
                            </CompletedCount>
                        </ConfettiContainer>
                        <Text>
                            {t('dailyPause:celebration_modal.congrats_text', {
                                count: data.scheduledContentActivity.completedCount || 1,
                            })}
                        </Text>
                        <WellbeingPrompt>
                            <Text>{t('dailyPause:celebration_modal.cta_text')}</Text>
                            <BooleanButtonGroup>
                                <Button
                                    type={improvedWellbeing === true ? 'accept' : 'white'}
                                    widthPercentage={49}
                                    onClick={() => onFoundHelpful(true)}
                                >
                                    {t('dailyPause:celebration_modal.cta_option.yes')}
                                </Button>
                                <Button
                                    type={improvedWellbeing === false ? 'accept' : 'white'}
                                    widthPercentage={49}
                                    onClick={() => onFoundHelpful(false)}
                                >
                                    {t('dailyPause:celebration_modal.cta_option.no')}
                                </Button>
                            </BooleanButtonGroup>
                        </WellbeingPrompt>
                    </ContentContainer>
                )}
            </ModalContainer>
        </Modal>
    );
};

const ModalContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '80vh',
});

const ContentContainer = styled.div({
    width: 400,
});

const ConfettiContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: 220,
});

const SuccessAnimation = styled(Lottie)({
    width: 400,
    maxHeight: 220,
    opacity: 0.7,
    position: 'absolute',
    overflow: 'hidden',
});

const ContentTitle = styled.p({
    ...font.black(font.size.largeExtended),
    color: color.ebonyClay,
    textAlign: 'center',
});

const CompletedCount = styled.p({
    ...font.black(font.size.enormous),
    color: color.ebonyClay,
    textAlign: 'center',
});

const Text = styled.p({
    ...font.medium(font.size.medium),
    lineHeight: 2,
    color: color.ebonyClay,
    textAlign: 'center',
    padding: '1rem',
});

const WellbeingPrompt = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    ...font.medium(font.size.medium),
    color: color.ebonyClay,
    minHeight: 240,
});

const BooleanButtonGroup = styled.div({
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'space-between',
});
